import { notFound } from "@tanstack/solid-router";
import { createServerFn } from "@tanstack/solid-start";
import { z } from "zod";
import { withSentrySpan } from "~/lib/sentry-span";
import { authFunctionMiddleware } from "~/server/middleware/auth";
import type { BankTransactionItem } from "./bank-transaction.server";

export const GetBankTransactionDetailPageDataInputSchema = z.object({
  businessId: z.string().trim().min(1),
  bankTransactionId: z.string().trim().min(1),
});

export type BankTransactionDetailPageData = {
  businessDetail: Awaited<
    ReturnType<
      typeof import("../business/business.server").getBusinessDetailForUser
    >
  >;
  bankTransaction: BankTransactionItem | null;
};

/**
 * Aggregate server function for the bank transaction detail page.
 *
 * Fetches business detail and bank transaction detail in a single HTTP round
 * trip on client-side navigation.
 */
export const getBankTransactionDetailPageData = createServerFn({
  method: "GET",
})
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) =>
    GetBankTransactionDetailPageDataInputSchema.parse(i),
  )
  .handler(
    async ({ context, data }): Promise<BankTransactionDetailPageData> => {
      return withSentrySpan("bankTransactionDetail.pageData", async () => {
        const [businessMod, bankTransactionMod] = await Promise.all([
          import("../business/business.server"),
          import("./bank-transaction.server"),
        ]);
        const { getBusinessDetailForUser } = businessMod;
        const { getBankTransactionForUser } = bankTransactionMod;

        const [businessDetail, bankTransaction] = await Promise.all([
          getBusinessDetailForUser(context.user.id, data.businessId),
          getBankTransactionForUser(
            context.user.id,
            data.businessId,
            data.bankTransactionId,
          ),
        ]);

        if (!bankTransaction && data.bankTransactionId !== "new") {
          // notFound() (not a bare Response) — thrown Responses can't be
          // serialized by the server RPC layer (Seroval crash → 500) and aren't
          // rejected properly on the client. notFound() renders the route's
          // notFoundComponent with a clean 404.
          throw notFound();
        }

        return { businessDetail, bankTransaction } as const;
      });
    },
  );
